import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Building2, Users, Send, Plus, X, CheckCircle2, ArrowRight, ArrowLeft, Mail, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { enterpriseService } from "../services/enterpriseService";
import { Alert, AlertDescription } from "./ui/alert";

interface TeamMember {
  email: string;
  key: number;
}

interface EnterpriseOnboardingFlowProps {
  onComplete: (data: any, userRole: string) => void;
  onLogout?: () => void;
  onOrgSetupComplete?: () => void;
}

export function EnterpriseOnboardingFlow({ onComplete, onLogout, onOrgSetupComplete }: EnterpriseOnboardingFlowProps) {
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadState, setLoadState] = useState<"loading" | "setup" | "request-join" | "request-sent" | "no-org">("loading");
  const [orgInfo, setOrgInfo] = useState<{ name: string; domain: string } | null>(null);

  const userEmail = typeof window !== "undefined" ? localStorage.getItem("userEmail") : "";

  useEffect(() => {
    determineFlow();
  }, []);

  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  useEffect(() => {
    if (loadState !== "request-sent") return;
  }, [loadState]);

  const checkMembershipStatus = async () => {
    if (isCheckingStatus) return;
    setIsCheckingStatus(true);
    try {
      const org = await enterpriseService.getOrganization();
      if (org) {
        const members = await enterpriseService.getMembers();
        const currentMember = (Array.isArray(members) ? members : []).find(
          m => m.email === localStorage.getItem('userEmail')
        );
        if (currentMember && currentMember.status === 'ACTIVE') {
          if (currentMember.role !== 'ORG_ADMIN') {
            toast.success("Your request to join has been approved!");
            redirectToIndividualOnboarding();
            return;
          }
          setLoadState("setup");
          return;
        }
      }
      toast.info("Your request is still pending approval from the organization admin.");
    } catch {
      toast.info("Your request is still pending approval from the organization admin.");
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const redirectToIndividualOnboarding = () => {
    localStorage.setItem('plan', 'INDIVIDUAL');
    localStorage.setItem('membershipStatus', 'ACTIVE');
    window.location.href = '/onboarding';
  };

  const goToOnboarding = () => {
    window.location.href = '/onboarding';
  };

  const determineFlow = async () => {
    try {
      const org = await enterpriseService.getOrganization().catch(() => null);
      if (org) {
        const currentEmail = localStorage.getItem('userEmail');
        try {
          const members = await enterpriseService.getMembers();
          const currentMember = (Array.isArray(members) ? members : []).find(
            m => m.email === currentEmail
          );
          if (currentMember && currentMember.role !== 'ORG_ADMIN' && currentMember.status=='ACTIVE') {
            redirectToIndividualOnboarding();
            return;
          }
          if (currentMember && currentMember.role !== 'ORG_ADMIN' && currentMember.status!='ACTIVE') {
            setOrgInfo({ name: org.name, domain: org.domain || "" });
            setLoadState("request-join");
            return;
          }
        } catch {
          // Role check failed, treat as admin
        }
        setOrgInfo({ name: org.name, domain: org.domain || "" });
        setOrgName(org.name || "");
        setIndustry(org.industry || "");
        setCompanySize(org.companySize || "");
        setLoadState("setup");
        return;
      }
    } catch {
      // Not a member
    }

    if (userEmail && userEmail.includes("@")) {
      const domain = userEmail.split("@")[1];
      try {
        const data = await enterpriseService.checkDomain(domain);
        if (data.exists) {
          setOrgInfo({ name: data.orgName, domain: data.orgDomain || domain });
          setLoadState("request-join");
          return;
        }
      } catch {
        // Domain check failed
      }
    }

    setLoadState("no-org");
  };

  const handleRequestAccess = async () => {
    setIsSubmitting(true);
    try {
      determineFlow();
      await enterpriseService.requestAccess();
      setLoadState("request-sent");
      toast.success("Your request to join has been sent to the organization admin.");
    } catch (error) {
      toast.error(error.message || "Failed to send request. Please contact your admin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipToDashboard = () => {
    localStorage.setItem("plan", "INDIVIDUAL");
    localStorage.setItem("onboardingComplete", "true");
    const userRole = localStorage.getItem("userRole") || "INVESTOR";
    onComplete({}, userRole);
  };

  const handleAddMember = () => {
    if (!inviteEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (teamMembers.some(m => m.email === inviteEmail)) {
      toast.error("This email has already been added");
      return;
    }
    setTeamMembers([...teamMembers, { email: inviteEmail, key: Date.now() }]);
    setInviteEmail("");
  };

  const handleRemoveMember = (key: number) => {
    setTeamMembers(teamMembers.filter(m => m.key !== key));
  };

  const handleNext = () => {
    if (step === 0) {
      if (!orgName.trim()) {
        toast.error("Please enter your organization name");
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const orgData = {
        name: orgName.trim(),
        industry: industry.trim() || undefined,
        companySize: companySize || undefined
      };

      await enterpriseService.completeOnboarding(orgData);

      if (teamMembers.length > 0) {
        for (const member of teamMembers) {
          try {
            await enterpriseService.inviteMember({ email: member.email });
          } catch (err) {
            console.warn(`Failed to invite ${member.email}:`, err);
          }
        }
      }

      localStorage.setItem("enterpriseOrgSetupDone", "true");
      localStorage.setItem("userData", JSON.stringify(orgData));
      onOrgSetupComplete?.();

      toast.success("Organization setup complete! Now complete your profile onboarding.");

      goToOnboarding();
    } catch (error) {
      toast.error(error.message || "Failed to complete onboarding");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      const orgData = {
        name: orgName.trim() || `${localStorage.getItem("fullName") || "My"} Organization`,
        industry: industry.trim() || undefined,
        companySize: companySize || undefined
      };

      await enterpriseService.completeOnboarding(orgData);
      localStorage.setItem("enterpriseOrgSetupDone", "true");
      onOrgSetupComplete?.();
      toast.success("Organization setup skipped! Now complete your profile onboarding.");
      goToOnboarding();
    } catch (error) {
      toast.error(error.message || "Failed to skip onboarding");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 2;
  const progressPercent = ((step + 1) / totalSteps) * 100;

  if (loadState === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (loadState === "request-sent") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <CardTitle>Request Sent</CardTitle>
                </div>
                <CardDescription>
                  Your request to join <strong>{orgInfo?.name}</strong> has been sent to the organization admin for approval.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Your request is pending approval from the organization admin. Click Refresh to check if your request has been approved.
                  </AlertDescription>
                </Alert>
                <Button onClick={checkMembershipStatus} disabled={isCheckingStatus} className="w-full">
                  {isCheckingStatus ? (
                    "Checking..."
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (loadState === "request-join") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold">Welcome to Your Enterprise Account</h1>
              <p className="text-muted-foreground mt-1">Your organization is already set up</p>
            </div>
            {onLogout && (
              <Button variant="outline" onClick={onLogout}>Logout</Button>
            )}
          </div>

          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-6 w-6 text-blue-600" />
                  <CardTitle>Join {orgInfo?.name}</CardTitle>
                </div>
                <CardDescription>
                  Your company already has an organization on WealthBrainAI. Request access to join.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="border-amber-200 bg-amber-50">
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>{orgInfo?.name}</strong> is already registered. Send a join request to the organization admin.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleRequestAccess}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      "Sending Request..."
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" /> Request to Join
                      </>
                    )}
                  </Button>
                  {/* <Button variant="outline" onClick={handleSkipToDashboard} className="flex-1">
                    <LogIn className="mr-2 h-4 w-4" /> Go to Dashboard
                  </Button> */}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Welcome to Your Enterprise Account</h1>
            <p className="text-muted-foreground mt-1">Set up your organization to get started</p>
          </div>
          {onLogout && (
            <Button variant="outline" onClick={onLogout}>Logout</Button>
          )}
        </div>

        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        <div className="max-w-2xl mx-auto">
          {step === 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-6 w-6 text-blue-600" />
                  <CardTitle>Organization Setup</CardTitle>
                </div>
                <CardDescription>
                  Tell us about your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name *</Label>
                  <Input
                    id="orgName"
                    placeholder="e.g. Acme Corp"
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    placeholder="e.g. Financial Services, Technology"
                    value={industry}
                    onChange={e => setIndustry(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companySize">Company Size</Label>
                  <select
                    id="companySize"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={companySize}
                    onChange={e => setCompanySize(e.target.value)}
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-1000">201-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleNext}>
                    Next Step <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 1 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-6 w-6 text-blue-600" />
                  <CardTitle>Invite Your Team</CardTitle>
                </div>
                <CardDescription>
                  Invite team members to join your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="inviteEmail">Email Address</Label>
                    <div className="flex gap-2">
                      <Input
                        id="inviteEmail"
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleAddMember())}
                      />
                      <Button
                        variant="outline"
                        onClick={handleAddMember}
                        className="mt-0"
                        type="button"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                </div>

                {teamMembers.length > 0 && (
                  <div className="space-y-2">
                    <Label>Team Members ({teamMembers.length})</Label>
                    <div className="space-y-2">
                      {teamMembers.map(member => (
                        <div
                          key={member.key}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{member.email}</span>
                            <Badge variant="secondary" className="text-xs">Pending</Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.key)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleSkip} disabled={isSubmitting}>
                      Skip
                    </Button>
                    <Button onClick={handleComplete} disabled={isSubmitting}>
                      {isSubmitting ? (
                        "Completing..."
                      ) : (
                        <>
                          Complete <CheckCircle2 className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default EnterpriseOnboardingFlow;
